import { retrieveFirmFileMetadata } from "@/lib/data/requests/firm/firm-file";
import {
  createOrdinaryFile,
  fileDestination,
  fileOperationMessage,
} from "@/lib/data/requests/files/canonical-files";
import { adaptFile } from "@/lib/data/requests/files/file-adapter";
import { attachFirmFile } from "@/lib/data/requests/chat-attachments";
import {
  createFirmFileV1,
  generateFirmFileDocumentV1,
  generateFirmFilePdfV1,
  retrieveFirmFilesV1,
  updateFirmFileV1,
  type GenerateFirmFileDocumentV1Input,
} from "@/lib/data/requests/firm/firm-file";
import { deleteFile } from "@/lib/data/requests/files/file";
import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import { queryKeys } from "@/lib/data/static";
import { type APIError, type FirmFile } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

type FirmFileParams =
  | {
      clientMethod: "put_object";
      fileName: string;
    }
  | {
      clientMethod: "get_object" | "delete_object";
      key: string;
    };

export const retrieveFirmFilesOptions = (enabled = true) => {
  return {
    queryKey: [queryKeys.firmFiles],
    queryFn: async () => retrieveFirmFilesV1(),
    enabled,
  } as const;
};

export const useCreateFirmFile = ({
  showToast = true,
  expectedMode,
}: { showToast?: boolean; expectedMode?: "create" } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Parameters<typeof createFirmFileV1>[0]) => {
      if (!data.isFolder && !data.file)
        return { ...(await createFirmFileV1(data)), publishedVersion: null };
      const destination = fileDestination("firm", data.idLocation);
      const result = await createOrdinaryFile({
        expectedMode,
        name: data.name,
        description: data.description,
        isFolder: data.isFolder,
        file: data.file,
        destination,
      });
      const metadata = result.isFolder
        ? undefined
        : (await retrieveFirmFileMetadata()).data.find(
            (item) => item.id === result.id,
          );
      let file = adaptFile<FirmFile>(result, { ...data, ...metadata });
      if (data.tags?.length) {
        file = (
          await updateFirmFileV1({
            id: result.id,
            dateModified: result.dateModified,
            tags: data.tags,
          })
        ).data;
      }
      return {
        data: file,
        publishedVersion: result.publishedVersion,
        meta: { userMsg: "File saved" },
      };
    },
    onError: (error: AxiosError<APIError>) => {
      if (showToast) {
        toast.error(fileOperationMessage(error, "Error creating firm file"));
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.files] });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmFiles],
      });
    },
    onSuccess: (response, variables) => {
      if (showToast) {
        toast.success(
          variables.isFolder
            ? "Folder created successfully"
            : variables.file
              ? response.publishedVersion
                ? "File Version Uploaded"
                : "File uploaded successfully"
              : "Firm file created successfully",
        );
      }
    },
  });
};

export const useUpdateFirmFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateFirmFileV1>[0]) =>
      updateFirmFileV1(data),
    onError: (error: AxiosError<APIError>) => {
      toast.error(fileOperationMessage(error, "Error updating firm file"));
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.files] });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmFiles],
      });
    },
    onSuccess: () => {
      toast.success("Firm file updated successfully");
    },
  });
};

export const useDeleteFirmFile = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Pick<FirmFile, "id"> & Partial<Pick<FirmFile, "isFolder">>,
    ) => deleteFile(data.id),
    onError: () => {
      if (showToast) {
        toast.error("Error deleting firm file");
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.files] });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmFiles],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Firm file deleted successfully");
      }
    },
  });
};

export const useGenerateFirmFilePreSignedUrl = () => {
  return useMutation({
    mutationFn: (data: FirmFileParams) =>
      generateS3ItemPreSignedUrlV1("firm-file")(data),
    onError: () => {
      toast.error("Error generating pre-signed URL");
    },
  });
};

export const useGenerateFirmFilePdf = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof generateFirmFilePdfV1>[0]) =>
      generateFirmFilePdfV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error converting firm file to PDF");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.files] });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmFiles],
      });
    },
    onSuccess: () => {
      toast.success("PDF created successfully");
    },
  });
};

export const useGenerateFirmFileDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: GenerateFirmFileDocumentV1Input) =>
      generateFirmFileDocumentV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error generating firm document");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.files] });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmFiles],
      });
    },
    onSuccess: () => {
      toast.success("Firm document generated successfully");
    },
  });
};

/** Chat uploads reuse an existing destination without overwriting it. */
export const useAttachFirmFile = () => {
  const create = useCreateFirmFile({
    showToast: false,
    expectedMode: "create",
  });
  return useMutation({
    mutationFn: (data: Parameters<typeof createFirmFileV1>[0]) =>
      attachFirmFile(data, create.mutateAsync),
  });
};
